import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import { Image, Platform, Pressable, StyleSheet, View, ViewProps } from 'react-native'

import { Color, Radius, Spacing } from '../../style'
import { Button } from '../button'
import { Icon } from '../icon'
import { ProgressBar } from '../progress'

import { Text } from '../typography'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 280,
  },
  content: {
    flex: 1,
  },
  dropzone: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    backgroundColor: Color.graphicGrey,
    borderColor: Color.metalGrey,
    borderWidth: 1,
    borderRadius: Radius.S,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  focus: {
    borderColor: Color.darkGreyBlue,
    borderStyle: 'solid',
  },
  hovered: {
    borderColor: Color.premiumBlack,
    borderWidth: 2,
    borderStyle: 'solid',
    margin: -1,
  },
  drag: {
    backgroundColor: Color.selectedBlue,
    borderColor: Color.darkGreyBlue,
    borderStyle: 'solid',
  },
  error: {
    borderColor: Color.soothingPink,
    borderStyle: 'solid',
  },
  disabled: {
    backgroundColor: Color.sleepingGrey,
  },
  disabledText: {
    color: Color.calmGrey,
  },
  icon: {
    backgroundColor: Color.simplyWhite,
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholder: {
    margin: Spacing.XS,
  },
  preview: {
    flex: 1,
    padding: Spacing.S,
    backgroundColor: Color.graphicGrey,
    borderColor: Color.premiumBlack,
    borderWidth: 1,
    borderRadius: Radius.S,
    alignItems: 'center',
  },
  image: {
    flex: 1,
    width: '100%',
    marginBottom: Spacing.S,
  },
  progress: {
    width: '100%',
    marginVertical: 15,
  },
  skeletonDropzone: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
})

export enum UploadFileError {
  FileInvalidType = 'file-invalid-type',
  FileTooLarge = 'file-too-large',
  FileTooSmall = 'file-too-small',
  TooManyFiles = 'too-many-files',
  Unknown = 'unknown',
}

export interface Props extends ViewProps {
  value?: string
  onValueChange?: (value?: string, file?: File) => void
  onError?: (error: UploadFileError) => void
  error?: boolean
  disabled?: boolean
  placeholder?: string | ReactElement
  placeholderDragging?: string | ReactElement
  title?: string | ReactElement
  deleteTitle?: string
  progress?: number
  options?: Omit<DropzoneOptions, 'onDrop' | 'disabled'>
  renderPreview?: (value?: string) => ReactElement
}

const UploadFile = (props: Props) => {
  const {
    value,
    onValueChange,
    onError,
    error,
    placeholder,
    placeholderDragging,
    title,
    deleteTitle,
    disabled,
    progress,
    options,
    renderPreview,
    style,
    ...otherProps
  } = props
  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      onError?.(fileRejections[0]?.errors[0]?.code as UploadFileError)
      return
    }
    onValueChange?.(URL.createObjectURL(acceptedFiles[0]), acceptedFiles[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject, open } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/bmp': [] },
    multiple: false,
    maxFiles: 1,
    maxSize: 20000000,
    minSize: 1000,
    disabled,
    noClick: true,
    ...options,
  })
  const [active, setActive] = useState(false)

  const updateActive = (value: boolean) => () => {
    setActive(value)
  }
  useEffect(() => {
    if (disabled) setActive(false)
  }, [disabled])

  const dropzoneStyle = useMemo(
    () => ({
      outline: 'none',
      ...styles.dropzone,
      ...(isFocused || active ? styles.focus : {}),
      ...(isDragActive ? styles.drag : {}),
    }),
    [active, isFocused, isDragAccept, isDragReject],
  )

  const remove = () => {
    onValueChange?.(undefined)
  }

  const renderImage = (state: { pressed: boolean; hovered?: boolean }) => {
    const { hovered } = state
    return (
      <div {...getRootProps({ style: { display: 'flex', flex: 1 } })} data-testid="UploadFile.Dropzone">
        <input {...getInputProps()} />
        {renderPreview ? (
          renderPreview(value)
        ) : (
          <View
            style={[
              dropzoneStyle,
              styles.preview,
              hovered && styles.hovered,
              error && styles.error,
              disabled && styles.disabled,
            ]}
          >
            <Image style={styles.image} source={{ uri: value }} resizeMode="contain" />
            {progress === undefined ? (
              <Button left="trash" title={deleteTitle} light onPress={remove} testID="UploadFile.Delete" />
            ) : (
              <ProgressBar value={progress} style={styles.progress} />
            )}
          </View>
        )}
      </div>
    )
  }

  const renderPlaceholder = () => {
    const content = (isDragActive && placeholderDragging) || placeholder
    if (!content) return
    return (
      <View style={styles.placeholder}>
        {typeof content === 'string' ? <Text style={disabled && styles.disabledText}>{content}</Text> : content}
      </View>
    )
  }

  const renderTitle = () => {
    if (!title) return
    return typeof title === 'string' ? <Text level={4}>{title}</Text> : title
  }

  const renderUpload = (state: { pressed: boolean; hovered?: boolean }) => {
    const { hovered } = state

    return (
      <div
        {...getRootProps({
          style: {
            ...dropzoneStyle,
            ...(hovered ? styles.hovered : {}),
            ...(error ? styles.error : {}),
            ...(disabled ? styles.disabled : {}),
          },
        })}
        data-testid="UploadFile.Dropzone"
      >
        {renderTitle()}
        <input {...getInputProps()} />
        <View style={styles.icon}>
          <Icon name="upload" size={32} color={disabled ? Color.calmGrey : undefined} />
        </View>
        {renderPlaceholder()}
      </div>
    )
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Pressable
        onFocus={updateActive(true)}
        onBlur={updateActive(false)}
        style={[
          styles.content,
          // @ts-expect-error outline style none is not defined
          Platform.select({
            web: {
              outline: 'none',
            },
          }),
        ]}
        onPress={open}
        disabled={disabled}
        testID="UploadFile.Pressable"
      >
        {value ? renderImage : renderUpload}
      </Pressable>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
UploadFile.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={[styles.content, styles.skeletonDropzone]} />
    </View>
  )
}

export default UploadFile
